/** @jsxImportSource @emotion/react */
import { css, useTheme } from "@emotion/react";
import { useLanguage } from "LanguageContext";
import AnalysisSummary from "../DashboardComponents/AnalysisSummary";
import RateFeedback from "../../../CommonUi/RateFeedback/RateFeedback";
import { useAnalysis } from "../FormComponents/AnalysisResultContext";
import Markdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import PositiveView from "../DashboardComponents/PositiveView";

interface SummaryProps {
  analysisData: any;
  isComplianceRefFrame?: boolean;
}

export default function SummaryCard(props: Readonly<SummaryProps>) {
  const theme = useTheme();
  const { t } = useLanguage();
  const { analysisContextResult } = useAnalysis();
  const cardStyles = css`
    display: flex;
    flex-direction: column;
    background-color: ${theme.colors.white};
    box-shadow: ${theme.shadows.medium};
    padding: ${theme.spacing["4xl"]};
    border-radius: ${theme.radius.sm};
    width: 100%; /* Prend 100% de la largeur */
    box-sizing: border-box; /* Inclut le padding dans la largeur totale */
    margin: ${theme.spacing["5xl"]} 0; /* Espacement vertical */
    .summary {
      font-family: ${theme.typography.body.fontFamily};
      font-size: ${theme.typography.body.fontSize};
      line-height: ${theme.typography.body.lineHeight};
      color: ${theme.colors.textDark};
      white-space: pre-line;
      margin-top: 10px;
      ul {
        list-style-type: '- ';
        white-space: normal;
        padding-inline-start: 10px;
      }
    }
    p {
      margin-top: 10px;
    }
  `;

  const titleStyles = css`
    font-family: ${theme.typography.heading3_medium.fontFamily};
    font-size: ${theme.typography.heading3_medium.fontSize};
    font-weight: ${theme.typography.heading3_medium.fontWeight};
    margin-bottom: ${theme.spacing.lg};
    color: ${theme.typography.heading3_medium.colors};
  `;

  const listStyles = css`
    display: flex;
    gap: ${theme.spacing.sm};
    margin-bottom: ${theme.spacing.lg};
  `;

  let referenceFrame = '';
  try {
    const front_name = props.analysisData?.ruleset?.map((val: { front_name: any; }) => t(val.front_name, null, analysisContextResult?.locale_code));
    const stage = props.analysisData?.ruleset?.map((val: { stage: any; }) => t(val.stage, null, analysisContextResult?.locale_code));
    referenceFrame = front_name?.map((val: any, indx: any) => {
        return `${front_name[indx]} ${stage[indx] ? `(${stage[indx]})` : ""}`
});
  } catch (error) {
    console.error("Error while trying to get the reference frame from the analysis data", error); 
  }

  const _summaryFeedbacks = props.analysisData?.feedbacks.filter((item: any) => item.view === "Summary");

  return (
    <div css={cardStyles}>
      <h2 css={titleStyles}>{t("analyse_result_summaryCard_title", null, analysisContextResult?.locale_code)}</h2>
      {props?.isComplianceRefFrame && <div css={listStyles}>
        <AnalysisSummary analysis={props.analysisData} large />
      </div>}
      {!props?.isComplianceRefFrame &&
        <PositiveView show="all" analysis={props.analysisData}  />
      }
      {
        !props?.analysisData?.full_scope && (
          <>
            <p className="summary">{props.analysisData?.screener_reasoning}</p>
            <p className="summary">
              { 
                (t("campaign_analzyed_against_principles", null, analysisContextResult?.locale_code).includes("%(framework_name)s")) ? (
                  t("campaign_analzyed_against_principles", null, analysisContextResult?.locale_code).replace("%(framework_name)s", referenceFrame)
                ) : (
                  t("campaign_analzyed_against_principles", {
                    framework_name: referenceFrame,
                  }, analysisContextResult?.locale_code)
                )
              }
            </p>
          </>
        )
      }
        <div className="summary">
          <Markdown rehypePlugins={[rehypeRaw]}>
            {props.analysisData?.analysis_summary}
          </Markdown>
      </div>
        <RateFeedback
          title={t("feedback_rate_summary", null, analysisContextResult?.locale_code)}
          feedbackPosition="Summary"
          feedbackDetails={_summaryFeedbacks?.[0]}
          id=""
          entityType="analysis_id"
          entityId={props.analysisData?.id}
          />
    </div>
  );
}
